import { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import type { CellKindConversionIntent } from '@atlassian/editor-ai-common/utils/normaliseEvents';

type CellKind = 'tableCell' | 'tableHeader';

type RawADFNode = {
	attrs?: unknown;
	content?: unknown;
	type?: unknown;
};

type RawCellMatch = {
	cell: RawADFNode;
};

type CellContext = {
	cell: PMNode;
	cellEnd: number;
	cellIndex: number;
	cellPos: number;
	rowIndex: number;
	table: PMNode;
};

export type CellKindConversionRejection = {
	message: string;
	reason:
		| 'partialCellRange'
		| 'missingTargetLocalId'
		| 'nonUniqueTargetLocalId'
		| 'invalidReplacementCell'
		| 'invalidCellAttributes'
		| 'invalidParentContent'
		| 'invalidTableGeometry';
};

export type CellKindConversionResult =
	| { status: 'notRequested' }
	| {
			cellEnd: number;
			cellPos: number;
			originalCell: PMNode;
			replacementCell: PMNode;
			status: 'ready';
	  }
	| ({ status: 'rejected' } & CellKindConversionRejection);

type PrepareCellKindConversionArgs = {
	adfResponse: unknown;
	cellKindConversionIntent?: CellKindConversionIntent;
	conversionActive?: boolean;
	doc: PMNode;
	endPos: number;
	originalCell?: PMNode;
	originalRangeIsFull?: boolean;
	startPos: number;
};

const CELL_NODE_NAMES = new Set<CellKind>(['tableCell', 'tableHeader']);
const VALIGN_VALUES = new Set(['top', 'middle', 'bottom']);

const REJECTION_MESSAGES: Record<CellKindConversionRejection['reason'], string> = {
	partialCellRange: 'Cell conversion requires the complete cell content range.',
	missingTargetLocalId: 'Cell conversion requires a target cell ID.',
	nonUniqueTargetLocalId: 'Cell conversion requires a unique target cell ID.',
	invalidReplacementCell: 'The replacement response does not contain a valid cell.',
	invalidCellAttributes: 'The replacement cell contains invalid attributes.',
	invalidParentContent: 'The replacement cell is not valid in its target row.',
	invalidTableGeometry: 'The replacement cell would produce invalid table geometry.',
};

const notRequested = (): CellKindConversionResult => ({ status: 'notRequested' });

const reject = (reason: CellKindConversionRejection['reason']): CellKindConversionResult => ({
	message: REJECTION_MESSAGES[reason],
	reason,
	status: 'rejected',
});

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCellKind(type: unknown): type is CellKind {
	return typeof type === 'string' && CELL_NODE_NAMES.has(type as CellKind);
}

function getRawCellMatch(adfResponse: unknown): RawCellMatch | undefined {
	if (!isRecord(adfResponse)) {
		return undefined;
	}

	const response = adfResponse as RawADFNode;
	if (isCellKind(response.type)) {
		return { cell: response };
	}

	if (
		response.type !== 'doc' ||
		!Array.isArray(response.content) ||
		response.content.length !== 1
	) {
		return undefined;
	}
	const table = response.content[0];
	if (isRecord(table) && isCellKind(table.type)) {
		return { cell: table };
	}
	if (
		!isRecord(table) ||
		table.type !== 'table' ||
		!Array.isArray(table.content) ||
		table.content.length !== 1
	) {
		return undefined;
	}
	const row = table.content[0];
	if (
		!isRecord(row) ||
		row.type !== 'tableRow' ||
		!Array.isArray(row.content) ||
		row.content.length !== 1
	) {
		return undefined;
	}
	const cell = row.content[0];
	if (!isRecord(cell) || !isCellKind(cell.type)) {
		return undefined;
	}

	return { cell: cell as RawADFNode };
}

function getRawCells(adfResponse: unknown): RawADFNode[] {
	if (!isRecord(adfResponse)) {
		return [];
	}

	const cells: RawADFNode[] = [];
	const pending: unknown[] = [adfResponse];
	const seen = new Set<object>();
	while (pending.length > 0) {
		const value = pending.pop();
		if (!isRecord(value) || seen.has(value)) {
			continue;
		}
		seen.add(value);
		if (isCellKind(value.type)) {
			cells.push(value as RawADFNode);
		}
		if (Array.isArray(value.content)) {
			value.content.forEach((child) => pending.push(child));
		}
	}
	return cells;
}

function findCellContext(doc: PMNode, startPos: number): CellContext | undefined {
	if (!Number.isInteger(startPos) || startPos < 0 || startPos > doc.content.size) {
		return undefined;
	}

	try {
		const $start = doc.resolve(startPos);
		let cellDepth: number | undefined;
		for (let depth = $start.depth; depth > 0; depth--) {
			if (isCellKind($start.node(depth).type.name)) {
				cellDepth = depth;
				break;
			}
		}
		if (cellDepth === undefined) {
			return undefined;
		}

		const rowDepth = cellDepth - 1;
		const tableDepth = rowDepth - 1;
		if (
			rowDepth <= 0 ||
			tableDepth <= 0 ||
			$start.node(rowDepth).type.spec.tableRole !== 'row' ||
			$start.node(tableDepth).type.spec.tableRole !== 'table'
		) {
			return undefined;
		}

		const cell = $start.node(cellDepth);
		const cellPos = $start.before(cellDepth);
		return {
			cell,
			cellEnd: cellPos + cell.nodeSize,
			cellIndex: $start.index(rowDepth),
			cellPos,
			rowIndex: $start.index(tableDepth),
			table: $start.node(tableDepth),
		};
	} catch {
		return undefined;
	}
}

function isPositiveSafeInteger(value: unknown): value is number {
	return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isValidColwidth(value: unknown): value is number[] | null {
	return (
		value === null ||
		(Array.isArray(value) &&
			value.every((width) => typeof width === 'number' && Number.isFinite(width) && width >= 0))
	);
}

function validateRawAttrs(
	rawCell: RawADFNode,
	supportedAttrs: Set<string>,
): { attrs: Record<string, unknown>; valid: true } | { valid: false } {
	if (rawCell.attrs === undefined) {
		return { attrs: {}, valid: true };
	}
	if (!isRecord(rawCell.attrs)) {
		return { valid: false };
	}

	const attrs = rawCell.attrs;
	if (Object.keys(attrs).some((name) => !supportedAttrs.has(name))) {
		return { valid: false };
	}
	if (
		('background' in attrs && attrs.background !== null && typeof attrs.background !== 'string') ||
		('colspan' in attrs && !isPositiveSafeInteger(attrs.colspan)) ||
		('rowspan' in attrs && !isPositiveSafeInteger(attrs.rowspan)) ||
		('colwidth' in attrs && !isValidColwidth(attrs.colwidth)) ||
		('localId' in attrs && attrs.localId !== null && typeof attrs.localId !== 'string') ||
		('valign' in attrs &&
			attrs.valign !== null &&
			(typeof attrs.valign !== 'string' || !VALIGN_VALUES.has(attrs.valign)))
	) {
		return { valid: false };
	}

	return { attrs, valid: true };
}

function getCellGeometry(
	attrs: Record<string, unknown>,
): { colspan: number; rowspan: number; valid: true } | { valid: false } {
	const { colspan, rowspan } = attrs;
	if (!isPositiveSafeInteger(colspan) || !isPositiveSafeInteger(rowspan)) {
		return { valid: false };
	}
	return { colspan, rowspan, valid: true };
}

function validateParentContent({
	cellIndex,
	replacementCell,
	rowIndex,
	table,
}: {
	cellIndex: number;
	replacementCell: PMNode;
	rowIndex: number;
	table: PMNode;
}): { replacementRow: PMNode; valid: true } | { valid: false } {
	try {
		const row = table.child(rowIndex);
		const replacementRow = row.copy(row.content.replaceChild(cellIndex, replacementCell));
		if (!replacementRow.type.validContent(replacementRow.content)) {
			return { valid: false };
		}
		replacementRow.check();
		return { replacementRow, valid: true };
	} catch {
		return { valid: false };
	}
}

function validateTableGeometry({
	replacementCell,
	replacementRow,
	rowIndex,
	table,
}: {
	replacementCell: PMNode;
	replacementRow: PMNode;
	rowIndex: number;
	table: PMNode;
}): boolean {
	try {
		const currentMap = TableMap.get(table);
		if (
			currentMap.problems?.length ||
			currentMap.width <= 0 ||
			currentMap.height !== table.childCount
		) {
			return false;
		}
		const replacementGeometry = getCellGeometry(replacementCell.attrs);
		if (
			!replacementGeometry.valid ||
			replacementGeometry.colspan > currentMap.width ||
			replacementGeometry.rowspan > currentMap.height
		) {
			return false;
		}

		const replacementTable = table.copy(table.content.replaceChild(rowIndex, replacementRow));
		replacementTable.check();
		const replacementMap = TableMap.get(replacementTable);
		return (
			!replacementMap.problems?.length &&
			currentMap.width === replacementMap.width &&
			currentMap.height === replacementMap.height &&
			currentMap.height === table.childCount &&
			replacementMap.height === table.childCount
		);
	} catch {
		return false;
	}
}

/**
 * Prepares an exact, closed-cell replacement when a raw ADF response explicitly changes cell kind.
 * The caller owns feature gating and transaction application.
 */
export function prepareCellKindConversion({
	adfResponse,
	cellKindConversionIntent,
	conversionActive = false,
	doc,
	endPos,
	originalCell,
	originalRangeIsFull,
	startPos,
}: PrepareCellKindConversionArgs): CellKindConversionResult {
	const rawResponse = cellKindConversionIntent ? cellKindConversionIntent.adf : adfResponse;
	const rawMatch = getRawCellMatch(rawResponse);
	const renderedMatch = getRawCellMatch(adfResponse);
	const context = findCellContext(doc, startPos);
	const currentKind = context?.cell.type.name;
	const originalKind =
		originalCell && isCellKind(originalCell.type.name) ? originalCell.type.name : undefined;
	if (!currentKind && !originalKind && !conversionActive) {
		return notRequested();
	}
	if (!rawMatch || !isCellKind(rawMatch.cell.type)) {
		const expectedKind = currentKind ?? originalKind;
		const isOppositeKind = (cell: RawADFNode) =>
			isCellKind(cell.type) && (!expectedKind || cell.type !== expectedKind);
		const containsOppositeKind =
			getRawCells(rawResponse).some(isOppositeKind) ||
			getRawCells(adfResponse).some(isOppositeKind);
		if (conversionActive || containsOppositeKind) {
			return reject('invalidReplacementCell');
		}
		return notRequested();
	}

	const expectedKind = currentKind ?? originalKind;
	const kindSwitchRequested =
		conversionActive || (expectedKind !== undefined && rawMatch.cell.type !== expectedKind);
	if (!kindSwitchRequested) {
		return notRequested();
	}

	if (originalRangeIsFull === false) {
		return reject('partialCellRange');
	}
	if (!context || !isCellKind(context.cell.type.name)) {
		return reject('invalidReplacementCell');
	}
	if (startPos !== context.cellPos + 1 || endPos !== context.cellEnd - 1) {
		return reject('partialCellRange');
	}
	const originalTargetCell =
		originalCell && isCellKind(originalCell.type.name) ? originalCell : context.cell;
	const originalLocalId = originalTargetCell.attrs.localId;
	const currentLocalId = context.cell.attrs.localId;
	const targetLocalId = originalCell ? originalLocalId : currentLocalId;
	if (typeof targetLocalId !== 'string' || targetLocalId.trim().length === 0) {
		return reject('missingTargetLocalId');
	}
	if (currentLocalId !== targetLocalId) {
		return reject('partialCellRange');
	}

	let localIdCount = 0;
	doc.descendants((node) => {
		if (node.attrs.localId === targetLocalId) {
			localIdCount++;
		}
		return localIdCount < 2;
	});
	if (localIdCount !== 1) {
		return reject('nonUniqueTargetLocalId');
	}

	const replacementType = doc.type.schema.nodes[rawMatch.cell.type];
	if (!replacementType || !isCellKind(replacementType.name)) {
		return reject('invalidReplacementCell');
	}
	const supportedAttrs = new Set(Object.keys(replacementType.spec.attrs ?? {}));
	if (!supportedAttrs.has('localId')) {
		return reject('invalidCellAttributes');
	}
	const rawAttrResult = validateRawAttrs(rawMatch.cell, supportedAttrs);
	if (!rawAttrResult.valid) {
		return reject('invalidCellAttributes');
	}

	let parsedCell: PMNode;
	let renderedCell: PMNode;
	try {
		parsedCell = PMNode.fromJSON(doc.type.schema, rawMatch.cell);
		parsedCell.check();
		if (!renderedMatch || renderedMatch.cell.type !== rawMatch.cell.type) {
			return reject('invalidReplacementCell');
		}
		renderedCell = PMNode.fromJSON(doc.type.schema, renderedMatch.cell);
		renderedCell.check();
	} catch {
		return reject('invalidReplacementCell');
	}
	if (parsedCell.type !== replacementType || !isCellKind(parsedCell.type.name)) {
		return reject('invalidReplacementCell');
	}

	const attrs: Record<string, unknown> = { ...parsedCell.attrs };
	for (const name of supportedAttrs) {
		if (name !== 'localId' && name in originalTargetCell.attrs) {
			attrs[name] = originalTargetCell.attrs[name];
		}
	}
	for (const name of Object.keys(rawAttrResult.attrs)) {
		if (name !== 'localId') {
			attrs[name] = rawAttrResult.attrs[name];
		}
	}
	attrs.localId = targetLocalId;

	const geometry = getCellGeometry(attrs);
	if (
		!geometry.valid ||
		(attrs.colwidth !== null &&
			(!Array.isArray(attrs.colwidth) || attrs.colwidth.length !== geometry.colspan))
	) {
		return reject('invalidCellAttributes');
	}

	const marks = parsedCell.marks.reduce(
		(currentMarks, mark) => mark.addToSet(currentMarks),
		originalTargetCell.marks,
	);

	let replacementCell: PMNode;
	try {
		replacementCell = parsedCell.type.create(attrs, renderedCell.content, marks);
		replacementCell.check();
	} catch {
		return reject('invalidReplacementCell');
	}
	const parentContentResult = validateParentContent({
		cellIndex: context.cellIndex,
		replacementCell,
		rowIndex: context.rowIndex,
		table: context.table,
	});
	if (!parentContentResult.valid) {
		return reject('invalidParentContent');
	}

	if (
		!validateTableGeometry({
			replacementCell,
			replacementRow: parentContentResult.replacementRow,
			rowIndex: context.rowIndex,
			table: context.table,
		})
	) {
		return reject('invalidTableGeometry');
	}

	return {
		cellEnd: context.cellEnd,
		cellPos: context.cellPos,
		originalCell: originalTargetCell,
		replacementCell,
		status: 'ready',
	};
}
